import { arrayRange, EmptyArray, groupBy, max, sortBy, uniqueBy } from 'lib/array/arrayUtils'
import type { TourRoomTypeLabels } from 'hooks/Tours/useTourRoomTypeLabels'
import tourRoomTypeMessages from 'hooks/Tours/tourRoomTypeMessages'
import {
  PACKAGE_UPGRADE_VALUES,
  TourOptionPackageUpgrades,
  TOUR_V2_FEMALE_TRAVELLER_TITLES,
  TOUR_V2_MALE_TRAVELLER_TITLES,
  getOfferPackageUpgradeDisplayTextMap,
  ALL_TOUR_PACKAGE_TYPE,
  TOUR_HERO_IMAGES_IDS_ROTATION,
  TOUR_V2_PACKAGE_OPTION_BASE,
  TOUR_V2_PACKAGE_OPTION_EXTENDED,
  TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE,
  tourOptionPackages,
  TOUR_HANDBACK_URGENCY_MAX_DAYS,
  TOUR_OPTIONAL_EXPERIENCE_BOOKING_DAYS_BEFORE_DEPARTURE,
} from 'constants/tours'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import switchFunc from 'lib/function/switchFunc'
import { addDays, intlDateFormatter, isSameDay, isValidDate, startOfDay, subDays } from 'lib/datetime/dateUtils'
import moment from 'moment'
import {
  INTL_DAY_MONTH_NAME_SHORT,
  INTL_DAY_ONLY,
  ISO_DATE_FORMAT,
} from 'constants/dateFormats'
import config from 'constants/config'
import type { IntlShape } from 'react-intl'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { formatCurrency } from 'lib/format/formatCurrencyIntl'

export function deduceGenderFromTitle(title: App.Title): App.Gender {
  if (TOUR_V2_FEMALE_TRAVELLER_TITLES.includes(title)) {
    return 'Female'
  }

  if (TOUR_V2_MALE_TRAVELLER_TITLES.includes(title)) {
    return 'Male'
  }

  return 'Other'
}

export function depositAmountStringBuilder(
  defaultDepositAmountPercentage: number,
  depositType: App.Tours.V2OfferDepositType | undefined,
  depositAmount: number | undefined,
  intl: IntlShape,
  currencyCode?: string,
  regionCode?: string,
) {
  const baseText = intl.formatMessage({
    id: 'XvH0_6XjAqNpiJYU6VwZ',
    defaultMessage: 'Secure with a',
  })

  if (depositType === 'flat') {
    const formattedAmount = formatDepositFlatAmount(depositAmount, currencyCode, regionCode)
    return {
      full: intl.formatMessage(
        {
          id: 'jTkkG1u0S-qqHfaGfCJ5',
          defaultMessage: '{baseText} {amount}/person deposit',
        },
        {
          baseText,
          amount: formattedAmount,
        },
      ),
      short: intl.formatMessage(
        {
          id: 'zs-q0Wdyyt3Ib_93-r3z',
          defaultMessage: '{amount}/person',
        },
        {
          amount: formattedAmount,
        },
      ),
    }
  }

  if (depositType === 'percentage') {
    return {
      full: intl.formatMessage(
        {
          id: 'UhLgBEADJ3pg7a7Ao6zZ',
          defaultMessage: '{baseText} {depositAmount}% deposit',
        },
        {
          baseText,
          depositAmount,
        },
      ),
      short: intl.formatMessage(
        {
          id: 'PDlmxRdqzFec3kAEWvDU',
          defaultMessage: '{depositAmount}%',
        },
        { depositAmount },
      ),
    }
  }

  return {
    full: intl.formatMessage(
      {
        id: '_9lY0x6J-UrNvEq1dh-j',
        defaultMessage: '{baseText} {defaultDepositAmountPercentage}% deposit',
      },
      {
        baseText,
        defaultDepositAmountPercentage,
      },
    ),
    short: intl.formatMessage(
      {
        id: 'Dc5__sRjL0RtWtUjp2Lq',
        defaultMessage: '{defaultDepositAmountPercentage}%',
      },
      { defaultDepositAmountPercentage },
    ),
  }
}

/**
 * Prefer the caller-supplied currency/region (from Geo). Fall back to the default
 * region rather than the UI locale's region — otherwise e.g. an Arabic UI (`ar-SA`)
 * on the AU site would incorrectly resolve to SA/SAR.
 */
function resolveDepositCurrency(
  currencyCode?: string,
  regionCode?: string,
): { currencyCode: string, regionCode: string } {
  const resolvedRegionCode = regionCode ?? config.defaultRegion
  const resolvedCurrencyCode = currencyCode
    ?? config.regions.find(region => region.code === resolvedRegionCode)?.currencyCode
    ?? 'USD'

  return { currencyCode: resolvedCurrencyCode, regionCode: resolvedRegionCode }
}

function formatDepositFlatAmount(
  depositAmount: number | undefined,
  currencyCode?: string,
  regionCode?: string,
): string {
  if (depositAmount === undefined) return ''

  const resolved = resolveDepositCurrency(currencyCode, regionCode)
  return formatCurrency(depositAmount, resolved.currencyCode, resolved.regionCode, {
    format: 'roundedDollar',
  })
}

const roomTypeUpgrade = new Set([
  'twinShare',
  'tripleShare',
  'quadShare',
  'multiShare',
])
export function hasRoomTypeUpgrade(roomType: App.Tours.V2OfferRoomType | undefined) {
  return roomTypeUpgrade.has(roomType ?? '')
}

const roomTypePackageUpgrade = new Set([
  'single_regular',
  'single_vip',
  'single_platinum',
  'twin_regular',
  'twin_vip',
  'twin_platinum',
])
export function hasRoomTypePackageUpgrade(roomType: App.Tours.V2OfferRoomType | undefined) {
  return roomTypePackageUpgrade.has(roomType ?? '')
}

// If the tag is "Offer ends in X days" it should use the urgency variant, otherwise positive variant
export function getUrgencyTagVariant(urgencyTag: string) {
  if (urgencyTag.toLocaleLowerCase().includes('ends in')) {
    return 'speed'
  }
  return 'positive'
}

export function getTourV2VariationDuration(variation: App.Tours.TourV2OfferVariation) {
  const lastDay = max(variation.itinerary ?? [], (item) => item.startDay)! // guaranteed to exist
  const daysCount = lastDay.startDay + lastDay.duration - 1

  return daysCount
}

export function getTourPricePerDay(pricePerPerson: number, memberPricePerPerson: number, durationInDays?: number) {
  if (durationInDays === undefined || durationInDays <= 0) return
  return { pricePerPersonPerDay: Math.round(pricePerPerson / durationInDays), memberPricePerPersonPerDay: Math.round(memberPricePerPerson / durationInDays) }
}

export function getTourValueAtPerDay(valuePerPerson?: number, durationInDays?: number) {
  if (!durationInDays || !valuePerPerson) return
  return Math.round(valuePerPerson / durationInDays)
}

export function getRoomTypeDisplayText(roomType: string, offerId: string, packageUpgradeOptionDisplayName?: string) {
  const [room, packageOption] = roomType.split('_')
  if (packageUpgradeOptionDisplayName) {
    return `${room} ${packageUpgradeOptionDisplayName.toLocaleLowerCase()}`
  }
  return packageOption ? `${room} ${getOfferPackageUpgradeDisplayTextMap(offerId).get(packageOption as PACKAGE_UPGRADE_VALUES)?.toLocaleLowerCase()}` : roomType
}

const TOUR_V2_ROOM_TYPE_CAPACITIES: Record<App.Tours.V2OfferRoomType, number> = {
  single: 1,
  twin: 2,
  twinShare: 1,
  triple: 3,
  tripleShare: 1,
  quad: 4,
  quadShare: 1,
  multiShare: 1,
  single_regular: 1,
  single_vip: 1,
  single_platinum: 1,
  twin_regular: 2,
  twin_vip: 2,
  twin_platinum: 2,
}
const roomTypeCapacitySwitchFunc = switchFunc(TOUR_V2_ROOM_TYPE_CAPACITIES, 1)
export function getRoomTypeCapacity(roomType: App.Tours.V2OfferRoomType | undefined): number {
  return roomTypeCapacitySwitchFunc(roomType ?? '')
}

const TOUR_V2_ROOM_TYPE: Partial<Record<App.Tours.V2OfferRoomType, string>> = {
  single_regular: 'regular',
  single_vip: 'vip',
  single_platinum: 'platinum',
  twin_regular: 'regular',
  twin_vip: 'vip',
  twin_platinum: 'platinum',
}
export const getPackageOptionType = switchFunc(TOUR_V2_ROOM_TYPE, '')

export function getPackageOptionDisplayText(packageOption: string, offerId: string) {
  const lowerCasedPackageOption = packageOption.toLowerCase()

  return getOfferPackageUpgradeDisplayTextMap(offerId).get(lowerCasedPackageOption as PACKAGE_UPGRADE_VALUES) ?? packageOption
}

export function normalizeTourPackageUpgradeValue(packageType?: string): PACKAGE_UPGRADE_VALUES | undefined {
  if (!packageType) return undefined

  const normalizedPackageType = packageType
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')

  if (normalizedPackageType.includes(PACKAGE_UPGRADE_VALUES.PLATINUM)) {
    return PACKAGE_UPGRADE_VALUES.PLATINUM
  }

  if (normalizedPackageType.match(/\bvip\b/)) {
    return PACKAGE_UPGRADE_VALUES.VIP
  }

  if (normalizedPackageType.includes(PACKAGE_UPGRADE_VALUES.REGULAR)) {
    return PACKAGE_UPGRADE_VALUES.REGULAR
  }

  return undefined
}

const TOUR_PACKAGE_UPGRADE_SORT_RANK = TourOptionPackageUpgrades.reduce<Record<PACKAGE_UPGRADE_VALUES, number>>((acc, packageUpgrade, index) => {
  acc[packageUpgrade] = index
  return acc
}, {} as Record<PACKAGE_UPGRADE_VALUES, number>)

export function sortTourPackageUpgradeItems<T>(items: Array<T>, getPackageType: (item: T) => string | undefined): Array<T> {
  return items
    .map((item, index) => {
      const normalizedPackageType = normalizeTourPackageUpgradeValue(getPackageType(item))
      const rank = normalizedPackageType ? TOUR_PACKAGE_UPGRADE_SORT_RANK[normalizedPackageType] : Number.MAX_SAFE_INTEGER

      return {
        item,
        index,
        rank,
      }
    })
    .sort((left, right) => {
      if (left.rank !== right.rank) return left.rank - right.rank
      return left.index - right.index
    })
    .map(({ item }) => item)
}

export function getTourPackageListForm(
  departures: Array<App.Tours.TourV2OfferDeparture>,
  tourId: string,
  intl: IntlShape,
): Array<{ type: string, name: string }> {
  const optionsAvailables = departures.flatMap(departure => departure.options).map(option => ({
    type: option.packageType!,
    name: getPackageOptionDisplayText(option.packageType!, tourId),
  }))
  const uniquePackageTypes = uniqueBy(optionsAvailables, (option) => option.type)
  const allPackageType = {
    type: ALL_TOUR_PACKAGE_TYPE,
    name: intl.formatMessage({
      id: 'wRo0YXAd-Qf7XqZFeCW_',
      defaultMessage: 'All packages',
    }),
  }
  return [allPackageType, ...uniquePackageTypes]
}

export function findCheapestTourV2Variation(
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary,
): App.Tours.TourV2OfferVariation | undefined {
  const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer)
  const cheapestDeparture = cheapestPurchasableOption && offer.departures[cheapestPurchasableOption.fkDepartureId]
  const cheapestVariation = cheapestDeparture && offer.variations[cheapestDeparture.fkVariationId]

  return cheapestVariation
}

const BASE_ROOM_TYPE_MAP: Record<string, keyof TourRoomTypeLabels> = {
  single: 'single',
  twin: 'twin',
  twinShare: 'twinShare',
  triple: 'triple',
  tripleShare: 'tripleShare',
  quad: 'quad',
  quadShare: 'quadShare',
  multiShare: 'multiShare',
  single_regular: 'single',
  single_vip: 'single',
  single_platinum: 'single',
  twin_regular: 'twin',
  twin_vip: 'twin',
  twin_platinum: 'twin',
}

/**
 * @deprecated Use `formatRoomTypeLocalised` with labels from `useTourRoomTypeLabels` in React components.
 */
export function formatRoomType(roomType: App.Tours.V2OfferRoomType | undefined): string | undefined {
  if (!roomType) return undefined
  const key = BASE_ROOM_TYPE_MAP[roomType]
  if (!key) return undefined
  return getCurrentIntl().formatMessage(tourRoomTypeMessages[key])
}

export function formatRoomTypeLocalised(roomType: string, labels: TourRoomTypeLabels): string | undefined {
  const key = BASE_ROOM_TYPE_MAP[roomType]
  return key ? labels[key] : undefined
}

export const formatPackageOptionType = (tourId: string) => {
  const PACKAGE_UPGRADE_TEXT = getOfferPackageUpgradeDisplayTextMap(tourId)
  const TOUR_V2_UPGRADE_ROOM: Partial<Record<App.Tours.V2OfferRoomType, string>> = {
    single_regular: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.REGULAR),
    twin_regular: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.REGULAR),
    single_vip: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.VIP),
    twin_vip: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.VIP),
    single_platinum: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.PLATINUM),
    twin_platinum: PACKAGE_UPGRADE_TEXT.get(PACKAGE_UPGRADE_VALUES.PLATINUM),
  }
  return switchFunc(TOUR_V2_UPGRADE_ROOM, '')
}

export interface TourAccommodationBookingStatus {
  preAccommodationBooked: boolean
  postAccommodationBooked: boolean
}

interface CheapestOfTourV2Offer {
  cheapestDeparture?: App.Tours.TourV2OfferDeparture
  cheapestPurchasableOption?: App.Tours.TourV2OfferPurchasableOption
  cheapestVariation?: App.Tours.TourV2OfferVariation
  hasSomeDiscounts?: boolean
  isDiscountOnTour?: boolean
}
export function findCheapestOfTourV2Offer(
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary,
  variationId?: App.Tours.TourV2OfferVariation['id'],
  isLuxPlusToursEnabled = false,
): CheapestOfTourV2Offer {
  const cheapestPurchasableOption = findCheapestTourV2PurchasableOption(offer, variationId, isLuxPlusToursEnabled)

  if (!cheapestPurchasableOption) {
    return {
      cheapestDeparture: undefined,
      cheapestPurchasableOption: undefined,
      cheapestVariation: undefined,
      hasSomeDiscounts: false,
      isDiscountOnTour: false,
    }
  }

  const cheapestDeparture = offer.departures[cheapestPurchasableOption.fkDepartureId]
  const cheapestVariation = offer.variations[cheapestDeparture.fkVariationId]

  const discountOnPurchasableOption = offer.purchasableOptions.filter((purchasableOption) => {
    return purchasableOption.fullPrice - purchasableOption.price > 0
  })
  const hasSomeDiscounts = discountOnPurchasableOption.length > 0 && offer.purchasableOptions.length !== discountOnPurchasableOption.length
  const isDiscountOnTour = discountOnPurchasableOption.length === offer.purchasableOptions.length

  return {
    cheapestDeparture,
    cheapestPurchasableOption,
    cheapestVariation,
    hasSomeDiscounts,
    isDiscountOnTour,
  }
}

export function findCheapestTourExperience(purchasableExperienceOptions: Array<App.Tours.TourV2OfferPurchasableOption>): App.Tours.TourV2OfferPurchasableOption | undefined {
  const cheapestPurchasableExperienceOptions = sortBy(purchasableExperienceOptions, (purchasableExperienceOption) => purchasableExperienceOption.price, 'asc')
  return cheapestPurchasableExperienceOptions.length > 0 ? cheapestPurchasableExperienceOptions[0] : undefined
}

export interface TourDiscount {
  hasSomeDiscount: boolean
  allIsDiscounted: boolean
}
export function findDiscountOnTour(offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary, variationId?: string, departureIds?: Array<string>): TourDiscount {
  let purchasableOptions = offer.purchasableOptions
  if (variationId) {
    purchasableOptions = purchasableOptions.filter(purchaseOption => purchaseOption.fkVariationId === variationId)
  } else if (departureIds) {
    purchasableOptions = purchasableOptions.filter(purchaseOption => departureIds.includes(purchaseOption.fkDepartureId))
  }
  const discountOnPurchasableOption = purchasableOptions.filter((purchasableOption) => {
    return purchasableOption.fullPrice - purchasableOption.price > 0
  })
  const hasSomeDiscount = discountOnPurchasableOption.length > 0 && purchasableOptions.length !== discountOnPurchasableOption.length
  const allIsDiscounted = discountOnPurchasableOption.length === purchasableOptions.length

  return {
    hasSomeDiscount,
    allIsDiscounted,
  }
}

export type LuxPlusLabelTagOptions = 'all' | 'price-only' | 'access-only' | 'none'

export function getTourTags(
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary,
  isLuxPlusToursAvailable?: boolean,
  luxPlusLabels: LuxPlusLabelTagOptions = 'all',
  departureId?: string,
): Array<App.OfferUrgencyLabel> {
  if (departureId) {
    const departure = offer.departures[departureId]
    return departure.urgencyTags
  }
  const { hasSomeDiscount, allIsDiscounted } = findDiscountOnTour(offer)

  const tags: Array<App.OfferUrgencyLabel> = []

  if (allIsDiscounted) {
    tags.push({ type: 'sale' })
  } else if (hasSomeDiscount) {
    tags.push({ type: 'departuresale' })
  }

  if (isLuxPlusToursAvailable) {
    if (
      offer.luxPlus.access === 'earlyAccess' &&
      offer.schedule &&
      (luxPlusLabels === 'all' || luxPlusLabels === 'access-only')
    ) {
      tags.push({ type: 'lux_plus_early_access' })
      if (new Date(offer.schedule.start) > new Date()) {
        tags.push({ type: 'lux_plus_general_release', end: offer.schedule.start })
      }
    }
  }

  if (offer.isAgentHubExclusive) {
    tags.push({ type: 'agenthub_exclusive' })
  }
  return [...tags, ...offer.urgencyTags]
}

export interface TourV2PricePerDayDetails {
  isDefaultPriceView: boolean
}

export function checkPurchasableOptionHasPackageUpgradeOrRoomType(
  purchasableOptions: Array<App.Tours.TourV2OfferPurchasableOption>,
): { hasPackageUpgrade: boolean, hasRoomUpgrade: boolean } {
  const hasPackageUpgrade = groupBy(purchasableOptions, purchasableOption => purchasableOption.packageType).size > 1
  const hasRoomUpgrade = purchasableOptions.some(purchasableOption => hasRoomTypeUpgrade(purchasableOption.roomType))
  return { hasPackageUpgrade, hasRoomUpgrade }
}

export function checkInventoryAvailable(roomInventory: Map<string, number>, singleRoomsCount: number, twinRoomsCount: number, packageOption: string): boolean {
  const singleRoomInventory = roomInventory.get(`single_${packageOption.toLowerCase()}`) ?? 0
  const twinRoomInventory = roomInventory.get(`twin_${packageOption.toLowerCase()}`) ?? 0
  return singleRoomInventory >= singleRoomsCount && twinRoomInventory >= twinRoomsCount * 2
}

function getDateStringRange(
  startDate: Date,
  endDate: Date,
  intl: IntlShape = getCurrentIntl(),
) {
  if (isSameDay(startDate, endDate)) return undefined
  const firstDate = startDate.getMonth() === endDate.getMonth() ?
      intlDateFormatter(startDate, INTL_DAY_ONLY, intl.locale) :
      intlDateFormatter(startDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale)

  return intl.formatMessage(
    {
      id: 'YS5_rCdCxDLnycoW6GZm',
      defaultMessage: '{startDate} - {endDate}',
    },
    {
      startDate: firstDate,
      endDate: intlDateFormatter(endDate, INTL_DAY_MONTH_NAME_SHORT, intl.locale),
    },
  )
}

function consolidateEmptyTourExperience(
  activityDay: App.Tours.TourExperienceByDay,
  endDay: number,
  locations: Array<string>,
  itineraryEndDate: Date,
  intl: IntlShape,
) {
  const rangeDay = [activityDay.day, endDay]
  const locationsConsolidate = [...activityDay.locations, ...locations]
  activityDay.title = intl.formatMessage(
    {
      id: 'vSQbLdmQiOISL4VyNLhN',
      defaultMessage: 'Day {startDay} - {endDay}: {locations}',
    },
    {
      startDay: rangeDay[0],
      endDay: rangeDay[1],
      locations: locationsConsolidate.join(' - '),
    },
  )
  activityDay.rangeDay = rangeDay
  activityDay.rangeDate = getDateStringRange(activityDay.date, itineraryEndDate, intl)
  activityDay.locations = locationsConsolidate
  return activityDay
}

export function getTourExperiencesByItineraryDay(
  tour: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary | undefined,
  variationId: string,
  departureId: string,
  intl: IntlShape,
  bookedStartDate?: string,
): Array<App.Tours.TourExperienceByDay> {
  if (!tour) {
    return []
  }
  const departure = tour.departures[departureId]
  const departureStartDate = departure?.startDate ?? bookedStartDate
  if (!departureStartDate) {
    return []
  }
  const itineraries = variationId ? tour.variations[variationId]?.itinerary ?? [] : []
  let indexToKeepForNoExperiences = 0
  return itineraries.reduce<Array<App.Tours.TourExperienceByDay>>((acc, itineraryItem) => {
    const itineraryId = itineraryItem.id
    const departureDate = new Date(departureStartDate)
    const itineraryDate = addDays(departureDate, itineraryItem.startDay - 1)
    const itineraryEndDate = addDays(itineraryDate, itineraryItem.duration - 1)
    const endDay = itineraryItem.startDay + itineraryItem.duration - 1
    const rangeDay = itineraryItem.duration > 1 ? [itineraryItem.startDay, endDay] : undefined
    const rangeDate = getDateStringRange(itineraryDate, itineraryEndDate, intl) ?? undefined
    const locations = itineraryItem.locationsVisited
    const titleConsolidate = intl.formatMessage(
      {
        id: 'mwxhYMkBeh10PVzcoDxV',
        defaultMessage: 'Day {dayOrRange}: {locations}',
      },
      {
        dayOrRange: rangeDay ?
            intl.formatMessage(
              {
                id: 'YQoNkDN_41lxBMXSVvbH',
                defaultMessage: '{startDay} - {endDay}',
              },
              {
                startDay: rangeDay[0],
                endDay: rangeDay[1],
              },
            ) :
          itineraryItem.startDay,
        locations: itineraryItem.locationsVisited.join(' - '),
      },
    )
    if (itineraryItem.experiences.length > 0) {
      // only skip going to next index if we're the first itinerary with experiences
      if ((indexToKeepForNoExperiences === 0 && acc[indexToKeepForNoExperiences]) || indexToKeepForNoExperiences > 0) indexToKeepForNoExperiences++
      if (rangeDay) {
        const [startDay, endDay] = rangeDay
        arrayRange(startDay, endDay).forEach(day => {
          const experiencesByDay = itineraryItem.experiences.filter(experience => experience.dayNumbers.includes(day))
          const date = addDays(departureDate, day - 1)
          const title = intl.formatMessage(
            {
              id: 'pEC1uoG1Be1o8esT9M0m',
              defaultMessage: 'Day {day}: {locations}',
            },
            {
              day,
              locations: itineraryItem.locationsVisited.join(' - '),
            },
          )
          if (experiencesByDay.length > 0) {
            acc[indexToKeepForNoExperiences] = { title, day, date, locations, experiences: experiencesByDay, itineraryId }
            indexToKeepForNoExperiences++
          } else {
            // keep index for no experiences on consecutive day
            if (acc[indexToKeepForNoExperiences]) {
              const experienceDay = acc[indexToKeepForNoExperiences]
              acc[indexToKeepForNoExperiences] = consolidateEmptyTourExperience(experienceDay, endDay, locations, itineraryEndDate, intl)
            } else {
              acc[indexToKeepForNoExperiences] = { title, day, date, locations, itineraryId }
            }
          }
        })
      } else {
        acc[indexToKeepForNoExperiences] = {
          title: titleConsolidate,
          day: itineraryItem.startDay,
          date: itineraryDate,
          experiences: itineraryItem.experiences,
          locations,
          itineraryId,
        }
        indexToKeepForNoExperiences++
      }
    } else {
      // keep index for no experiences on consecutive day
      if (acc[indexToKeepForNoExperiences]) {
        const experienceDay = acc[indexToKeepForNoExperiences]
        acc[indexToKeepForNoExperiences] = consolidateEmptyTourExperience(experienceDay, endDay, locations, itineraryEndDate, intl)
      } else {
        acc[indexToKeepForNoExperiences] = {
          itineraryId,
          title: titleConsolidate,
          day: itineraryItem.startDay,
          date: itineraryDate,
          locations,
          rangeDay,
          rangeDate,
        }
      }
    }
    return acc
  }, [])
}

export function checkTourExperienceTimeSlot(
  tourExperienceItems: Array<App.Checkout.TourV2ExperienceItem>,
  timeSlots: Array<string>,
  tourExperienceDate: Date,
) {
  const dateTourExperience = moment(tourExperienceDate).format(ISO_DATE_FORMAT)
  if (timeSlots.length === 0) return true
  return timeSlots.some((timeSlot) => {
    const [candidateStart, candidateEnd] = timeSlot.split(' - ')
    const tourExperienceStartDate = Date.parse(`${dateTourExperience}T${candidateStart}`)
    const tourExperienceEndDate = Date.parse(`${dateTourExperience}T${candidateEnd}`)
    return tourExperienceItems.every((tourExperienceItem) => {
      if (!tourExperienceItem?.purchasableOption?.timeSlot) return true
      const [existingStart, existingEnd] = tourExperienceItem?.purchasableOption?.timeSlot?.split(' - ') ?? []
      const startDate = Date.parse(`${tourExperienceItem.date}T${existingStart}`)
      const closeDate = Date.parse(`${tourExperienceItem.date}T${existingEnd}`)
      return !(tourExperienceStartDate >= startDate && tourExperienceStartDate <= closeDate) &&
        !(tourExperienceEndDate >= startDate && tourExperienceEndDate <= closeDate) &&
        !(tourExperienceStartDate <= closeDate && tourExperienceEndDate >= startDate) &&
        !(tourExperienceStartDate <= startDate && tourExperienceEndDate >= closeDate)
    })
  })
}

export function getTourV2SavingsWithLuxPlus(purchasableOption: App.Tours.TourV2OfferPurchasableOption | undefined): number | undefined {
  const { price: pricePerPerson, memberPrice } = purchasableOption ?? {}
  const savingsWithLuxPlus = (!pricePerPerson || !memberPrice) ? undefined : Number(pricePerPerson.toFixed()) - Number(memberPrice.toFixed())
  return savingsWithLuxPlus
}

export function getTourItemKey(tourItem: App.Checkout.TourV2Item): string {
  return tourItem.privateRequestKey ? `${tourItem.offerId}-${tourItem.privateRequestKey}` : tourItem.offerId
}

export function isCurrentDepartureEligibleForDateChange(tour: App.Tours.TourV2Offer, tourOrderItem: App.Tours.TourV2OrderItem): boolean {
  const handbackDate = tour.departures[tourOrderItem.departureId]?.handbackDate
  return !!handbackDate && new Date(handbackDate) > new Date()
}

function getCalendarDaysRemaining(targetDate: Date, now: Date): number | undefined {
  if (!isValidDate(targetDate)) return undefined
  const daysRemaining = Math.round(
    (startOfDay(targetDate).getTime() - startOfDay(now).getTime()) / (1000 * 60 * 60 * 24),
  )
  return daysRemaining >= 0 ? daysRemaining : undefined
}

export function getDaysUntilHandback(handbackDate?: string, now: Date = new Date()): number | undefined {
  if (!handbackDate) return undefined
  return getCalendarDaysRemaining(new Date(handbackDate), now)
}

export function getDaysUntilOptionalExperiencePurchaseCutoff(
  departureStartDate?: string,
  bookingDaysBeforeDeparture: number = TOUR_OPTIONAL_EXPERIENCE_BOOKING_DAYS_BEFORE_DEPARTURE,
  now: Date = new Date(),
): number | undefined {
  if (!departureStartDate) return undefined
  const departureDate = new Date(departureStartDate)
  if (!isValidDate(departureDate)) return undefined
  return getCalendarDaysRemaining(
    subDays(startOfDay(departureDate), bookingDaysBeforeDeparture + 1),
    now,
  )
}

export function isWithinTourHandbackUrgencyWindow(daysUntilHandback: number): boolean {
  return daysUntilHandback >= 0 && daysUntilHandback <= TOUR_HANDBACK_URGENCY_MAX_DAYS
}

export function isTourProductTypeStyled(productType: App.Tours.V2ProductType): productType is App.Tours.V2ProductTypeStyled {
  return productType === 'deluxe_tour' || productType === 'premium_tour' || productType === 'ultra_lux_tour' || productType === 'signature_series_tour' || productType === 'custom_tour' || productType === 'insider_series_tour'
}
export function isTourProductTypeLabelled(productType: App.Tours.V2ProductType): productType is Exclude<App.Tours.V2ProductTypeStyled, App.Tours.V2CustomProductType> {
  return productType === 'deluxe_tour' || productType === 'premium_tour' || productType === 'ultra_lux_tour' || productType === 'signature_series_tour' || productType === 'insider_series_tour'
}

interface TourVariationLabelParams {
  productType: App.Tours.V2ProductType
  packageOptionName?: string
  intl: IntlShape
}

export function getTourVariationLabel(params: TourVariationLabelParams): string {
  const { productType, packageOptionName, intl } = params
  const hasExtension = tourOptionPackages.includes(packageOptionName ?? '')
  const isBase = (hasExtension && packageOptionName === TOUR_V2_PACKAGE_OPTION_BASE) || !hasExtension

  if (!hasExtension) {
    return ''
  }

  if (productType === 'custom_tour') {
    if (isBase) {
      return intl.formatMessage({
        id: 'CaQ-9x5ydbZCHP2d-8UU',
        defaultMessage: 'Deluxe',
      })
    }
    if (packageOptionName === TOUR_V2_PACKAGE_OPTION_EXTENDED) {
      return intl.formatMessage({
        id: 'VyMsWzxjobFcBCU7csvz',
        defaultMessage: 'Superior',
      })
    }
    if (packageOptionName === TOUR_V2_PACKAGE_OPTION_EXTENDED_ALTERNATIVE) {
      return intl.formatMessage({
        id: 'kmwcjG5mCvAfHq8K0edd',
        defaultMessage: 'Lux Upgrade',
      })
    }
  }

  return isBase ? intl.formatMessage({
    id: '40Eo64Jgu-tPv7Z5i8Ba',
    defaultMessage: 'Base',
  }) : intl.formatMessage({
    id: 'C9_sFi28dhfPOgkZZAPM',
    defaultMessage: 'Extension',
  })
}

export function shouldShowAdditionalTourWays(productType: App.Tours.V2ProductType): boolean {
  return productType !== 'custom_tour'
}

export function isTourLimitedTimeOffer(tour: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary): boolean {
  return tour.name?.includes('Limited Time Offer') ?? false
}

export function getStartEndPropertyId(tour: App.Tours.TourV2Offer, variationId: string): { startPropertyId: string | undefined, endPropertyId: string | undefined } {
  const recommandedHotel = tour.recommendedHotelAddOns?.[variationId]
  return {
    startPropertyId: recommandedHotel?.startBedbank?.[0] ? `bedbank:${recommandedHotel.startBedbank[0]}` : undefined,
    endPropertyId: recommandedHotel?.endBedbank?.[0] ? `bedbank:${recommandedHotel.endBedbank[0]}` : undefined,
  }
}

export function getMonthlyTourHomepageHeroImageId() {
  const date = new Date()
  const month = date.getMonth()
  const rotationIndex = month % TOUR_HERO_IMAGES_IDS_ROTATION.length
  return TOUR_HERO_IMAGES_IDS_ROTATION[rotationIndex]
}

export function getPrePostAccommodationStatus(order: App.Order): TourAccommodationBookingStatus {
  const bookingStatus = {
    preAccommodationBooked: false,
    postAccommodationBooked: false,
  }
  const tourItem = order.tourItems.find(tourItem => tourItem.status !== 'cancelled')
  if (!tourItem) return bookingStatus
  const bedBankItems = order.bedbankItems
  const tourStartDate = tourItem.tour.startDate
  const tourEndDate = tourItem.tour.endDate

  bedBankItems.forEach(bedBank => {
    const bedBankCheckIn = new Date(bedBank.checkIn.format('YYYY-MM-DD'))
    if (bedBankCheckIn <= new Date(tourStartDate)) {
      bookingStatus.preAccommodationBooked = true
    }
    if (bedBankCheckIn >= new Date(tourEndDate)) {
      bookingStatus.postAccommodationBooked = true
    }
  })
  return bookingStatus
}

export function getPrePostAccommodationDetails(order: App.Order): [
  preTourAccommodations: Array<App.OrderBedbankItem>,
  postTourAccommodations: Array<App.OrderBedbankItem>,
] {
  const tourItem = order.tourItems.find(tourItem => tourItem.status === 'completed')
  if (!tourItem) return [EmptyArray, EmptyArray]
  const bedBankItems = order.bedbankItems
  const tourStartDate = tourItem.tour.startDate
  const tourEndDate = tourItem.tour.endDate
  return [
    bedBankItems.filter((bedbank) => bedbank.checkIn.toDate() <= new Date(tourStartDate)),
    bedBankItems.filter((bedbank) => bedbank.checkIn.toDate() >= new Date(tourEndDate)),
  ]
}
